import { useEffect } from "react";

// Phase K3 — uniform ESC-to-close handling for modal dialogs.
//
// MUI's `Dialog` already wires up ESC to the `onClose` callback, but the
// behavior isn't consistent across our modals:
//   - Custom `Modal` wrappers (e.g. the config modal in DeviceEditorShell)
//     don't always ship with ESC handling.
//   - Some nested editors (Monaco, the terminal, rich TextFields) stop
//     keydown propagation, which means MUI's Dialog-scoped listener never
//     sees the Escape keystroke and the dialog stays open.
//   - Dialogs that are performing a blocking action (a deploy in progress)
//     need to swallow ESC so users don't accidentally close the dialog mid-
//     flight.
//
// This hook installs a single window-level `keydown` listener while the
// dialog is open and invokes `onClose` on Escape. When `busy` is true the
// listener consumes the keystroke (via preventDefault + stopPropagation) so
// the action can run to completion without interruption from any ancestor
// escape handlers.
//
// Usage:
//   useDialogEscape({ enabled: open, onClose, busy: deploying });
export default function useDialogEscape({ enabled, onClose, busy = false } = {}) {
  useEffect(() => {
    if (!enabled) return undefined;

    const handler = (event) => {
      if (event.key !== "Escape") return;
      if (event.defaultPrevented) return;

      if (busy) {
        // Swallow ESC while the dialog is busy so neither this dialog nor
        // any parent handler reacts to it.
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (typeof onClose === "function") {
        event.preventDefault();
        onClose();
      }
    };

    // Capture phase so nested editors that stopPropagation on keydown don't
    // prevent ESC from dismissing the dialog.
    window.addEventListener("keydown", handler, true);
    return () => window.removeEventListener("keydown", handler, true);
  }, [enabled, onClose, busy]);
}
